import {NeoButton, NeoSelect} from "@/components/forms";
import {FileChip} from "@/components/pages/vehicles/FileChip";
import {vehicleService} from "@/service/vehicle.service";
import {yupResolver} from "@hookform/resolvers/yup";
import {Card, CardBody, SelectItem} from "@nextui-org/react";
import {Label} from "flowbite-react";
import {useEffect, useRef, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {AiOutlineLoading} from "react-icons/ai";
import {PiNotebookDuotone} from "react-icons/pi";
import * as yup from "yup";

type DocumentsProps = {
  datas: {
    documentTypes: any[];
    trackerId: number;
    clientdata: any;
  };
  onRemove: (category: string, index: number) => void;
};

type UploadedFile = {
  id: any;
  name: string;
  file: string;
  fileSize: string;
  type: string;
};

export const Documents = ({datas, onRemove}: DocumentsProps) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [trackerId, setTrackerId] = useState<any>(null);
  const [sourceType, setSourceType] = useState<any>(null);
  const [clientdata, setClientData] = useState<any>(null);
  const [documentTypes, setDocumentTypes] = useState<any>([]);
  const documentTypeKeyMap: Record<number, string> = {
    4: "regDocuments",
    2: "insuranceDocuments",
    3: "permitDocuments",
    5: "pollutionDocuments",
    6: "fcDocuments",
    7: "taxDocuments",
    8: "greenTaxDocuments"
  };
  const [documentDataMap, setDocumentDataMap] = useState<Record<string, any[]>>(
    {
      regDocuments: [],
      insuranceDocuments: [],
      permitDocuments: [],
      pollutionDocuments: [],
      fcDocuments: [],
      taxDocuments: [],
      greenTaxDocuments: []
    }
  );

  useEffect(() => {
    setSourceType(1);
    setTrackerId(datas.trackerId);
    setDocumentTypes(datas.documentTypes);
    setClientData(datas.clientdata);
  }, [datas]);

  useEffect(() => {
    if (trackerId && documentTypes) {
      fetchDocuments();
    }
  }, [trackerId, documentTypes]);

  const fetchDocuments = async (documentType?: number) => {
    let requestdata: any;
    if (documentType) {
      requestdata = {
        sourceType: sourceType,
        trackerId: trackerId,
        documentType: documentType
      };
    } else {
      requestdata = {
        sourceType: sourceType,
        trackerId: trackerId
      };
    }

    try {
      const response = await vehicleService.ondocumentsread(requestdata);
      if (response?.data?.status === "success") {
        const documentdatas = response?.data?.data;
        if (documentdatas) {
          if (documentType) {
            const documentTypeKey = documentTypeKeyMap[documentType];
            const documents = documentdatas[documentTypeKey];
            if (documentTypeKey && Array.isArray(documents)) {
              setDocumentDataMap(prev => ({
                ...prev,
                [documentTypeKey]: documents
              }));
            }
          } else {
            Object.entries(documentTypeKeyMap).forEach(([id, key]) => {
              const list = documentdatas[key];
              if (Array.isArray(list)) {
                setDocumentDataMap(prev => ({
                  ...prev,
                  [key]: list
                }));
              }
            });
          }
        }
      } else if (response?.data?.errors?.length === 0) {
        toast.error(response?.data?.message);
      }
    } catch (err: any) {
      if (err?.errors?.length > 0) {
        err.errors.forEach(
          ({field, message}: {field: any; message: string}) => {
            toast.error(message);
          }
        );
      }
    }
  };

  const toggleUploadForm = (documentType?: any) => {
    const safeValue = documentType ? String(documentType) : "";
    reset({documentType: ""});
    setValue("documentType", safeValue);
    setShowPanel(true);
  };

  const formatSize = (bytes: number) => {
    const kb = bytes / 1024;
    return kb > 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFiles = (files: FileList) => {
    if (uploadedFiles.length >= 3) {
      toast.error("Maximum 3 files can be upload");
    } else {
      const newRandom = Math.floor(Math.random() * 9000) + 1000;
      const allowedTypes = ["image/", "application/pdf"];
      const formatted = Array.from(files)
        .filter(file => allowedTypes.some(type => file.type.startsWith(type)))
        .map<UploadedFile>(file => ({
          id: newRandom,
          name: file.name,
          file: URL.createObjectURL(file),
          fileSize: formatSize(file.size),
          type: file.type
        }));
      setUploadedFiles(prev => [...prev, ...formatted]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Required to allow drop
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const onRemovePrevurl = (fileToRemove: UploadedFile) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileToRemove.id));
    URL.revokeObjectURL(fileToRemove.file);
  };

  const validationSchema = yup.object().shape({
    documentType: yup.string().required("Please enter vehicle capacity")
  });

  const {
    register,
    handleSubmit,
    formState: {isSubmitting, isValid, errors},
    control,
    setValue,
    setError,
    reset
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = async (data: any) => {
    if (uploadedFiles.length == 0) {
      toast.error("Upload file is required");
    } else {
      const documentType: any = data.documentType;
      const newRandom = Math.floor(Math.random() * 9000) + 1000;
      const filename =
        documentTypeKeyMap[documentType] ?? "Document" + newRandom;
      const formData = new FormData();
      formData.append("trackerId", trackerId);
      formData.append("sourceType", sourceType);
      formData.append("documentType", documentType);
      formData.append("name", filename);
      for (let i = 0; i < uploadedFiles.length; i++) {
        const item = uploadedFiles[i];
        const response = await fetch(item.file);
        const blob = await response.blob();
        const file = new File([blob], item.name, {type: item.type});
        formData.append(`file[${i}]`, file);
      }
      /*for (const [key, value] of formData.entries()) {
        console.log(key, value);
      }*/
      const additionalPrimaryData = {
        insId: clientdata?.institutionId,
        currentUser: clientdata?.institutionUserId
      };

      try {
        const response = await vehicleService.ondocumentuploads(
          additionalPrimaryData,
          formData
        );
        if (response?.data?.status === "success") {
          toast.success(response?.data?.message);
          resetForm();
          setShowPanel(false);
          await fetchDocuments(documentType);
        } else if (response?.data?.errors?.length === 0) {
          toast.error(response?.data?.message);
        } else {
          toast.error(response?.data?.message);
        }
      } catch (err: any) {
        if (err?.errors?.length > 0) {
          err.errors.forEach(
            ({field, message}: {field: any; message: string}) => {
              toast.error(message);
            }
          );
        }
      } finally {
        //setLoading(false);
      }
    }
  };

  const resetForm = () => {
    setUploadedFiles([]);
    reset({documentType: ""});
    setTimeout(() => {
      reset();
    }, 100);
  };

  return (
    <div className="flex flex-row gap-2">
      {/* Left Panel */}
      <div
        className={`h-full flex-shrink-0 transition-all duration-300 ${
          showPanel ? "w-[300px] p-4" : "w-[35px] p-2"
        } relative flex flex-col overflow-hidden rounded-lg border-r bg-white shadow`}
      >
        {/* Always-visible Toggle Button */}
        <button
          onClick={async () => {
            await resetForm();
            setShowPanel(prev => !prev);
          }}
          className={`transition-all duration-200 ${
            showPanel
              ? "absolute right-2 top-2 p-1"
              : "flex items-center justify-center"
          } rounded`}
        >
          <PiNotebookDuotone className="h-5 w-5 text-gray-600" />
        </button>

        {/* Panel Content */}
        {showPanel && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-2">
              <Controller
                control={control}
                name="documentType"
                render={({field}) => (
                  <NeoSelect
                    label="Upload Document"
                    id="document"
                    placeholder="Please select document"
                    className="mb-3"
                    selectedKeys={field.value ? [String(field.value)] : []}
                    onSelectionChange={keys => {
                      const selected = Array.from(keys)[0];
                      field.onChange(
                        selected !== undefined ? Number(selected) : null
                      );
                    }}
                    color={errors.documentType ? "danger" : "default"}
                    isInvalid={!!errors.documentType}
                    errorMessage={errors.documentType?.message}
                  >
                    {documentTypes.map((e: any) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </NeoSelect>
                )}
              />
              <div
                className="flex h-20 w-full cursor-pointer items-center justify-center rounded-md border-2 border-dashed bg-green-50 text-sm text-green-700"
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                Click to Upload or drag and drop
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              <p className="mb-4 text-xs text-blue-700">
                <span className="font-bold text-red-500">*</span> Maximum 3
                files can be uploaded.
              </p>
              <div className="grid-col grid">
                {uploadedFiles.length > 0 ? (
                  uploadedFiles.map((file: any, idx: any) => (
                    <FileChip
                      key={idx}
                      file={file}
                      onRemove={() => onRemovePrevurl(file)}
                      classNames="w-full mb-2"
                    />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-6">
                    <img
                      src="/images/nodata.svg"
                      alt="No Documents"
                      className="max-w-[120px] opacity-70"
                    />
                    <p className="mt-2 text-sm text-gray-500">
                      No uploaded files
                    </p>
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <NeoButton
                  className=""
                  type="reset"
                  color="white"
                  onPress={() => resetForm()}
                >
                  Cancel
                </NeoButton>
                <NeoButton
                  className="rounded-md bg-green-600 hover:bg-green-700"
                  type="submit"
                  disabled={isSubmitting}
                  isLoading={loading}
                  spinner={
                    <AiOutlineLoading
                      size="xs"
                      className="animate-spin h-6 w-6"
                    />
                  }
                >
                  Upload
                </NeoButton>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Right Panel */}
      <div
        className={`grid w-full gap-4 ${
          showPanel
            ? "grid-cols-1 md:grid-cols-2"
            : "grid-cols-1 md:grid-cols-3"
        }`}
      >
        {documentTypes.map((doc: any) => {
          const key = documentTypeKeyMap[doc.id];
          const files = key ? documentDataMap[key] : [];

          return (
            <Card key={doc.id}>
              <CardBody>
                <Label
                  className="-mt-2 block p-2 text-xl font-bold"
                  value={`${doc.label} Document`}
                />
                {files.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                      {files.map(fileItem => (
                        <FileChip
                          key={fileItem.id}
                          file={fileItem}
                          onRemove={() => onRemove(fileItem.id, doc.id)}
                          classNames="w-full"
                        />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex cursor-pointer items-center justify-center py-6"
                    onClick={() => toggleUploadForm(doc.id)}
                  >
                    <img
                      src="/images/nodata.svg"
                      alt="No Documents"
                      className="max-w-[120px] opacity-70"
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
